import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Navbar from "./components/Navbar";
import AnimatedBackground from "./components/AnimatedBackground";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";

const SkillsPage = lazy(() => import("./pages/Skills"));
const ExperiencePage = lazy(() => import("./pages/Experience"));
const EducationPage = lazy(() => import("./pages/Education"));
const ProjectsPage = lazy(() => import("./pages/Projects"));
const BlogsPage = lazy(() => import("./pages/Blogs"));
const ContactPage = lazy(() => import("./pages/Contact"));
const NotFound = lazy(() => import("./pages/NotFound"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const RecommendationsPage = lazy(() => import("./pages/Recommendations"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div
    className="min-h-screen flex items-center justify-center text-gray-400"
    role="status"
    aria-live="polite"
  >
    Loading page…
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <HelmetProvider>
        <AnimatedBackground />
        <a
          href="#main-content"
          className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[100] bg-teal text-navy-dark px-3 py-2 rounded"
        >
          Skip to main content
        </a>
        <BrowserRouter>
          <Navbar />
          <ScrollToTop />
          <main id="main-content" className="block">
            <Suspense fallback={<RouteFallback />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/skills" element={<SkillsPage />} />
                <Route path="/experience" element={<ExperiencePage />} />
                <Route path="/education" element={<EducationPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/blogs" element={<BlogsPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/recommendations" element={<RecommendationsPage />} />
                <Route path="/blogs/:id" element={<BlogPost />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </main>
        </BrowserRouter>
      </HelmetProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
